/**
 * useUnifiedGridUpdate - Unified inline and mass update hook
 *
 * Combines the functionality of useInlineMassUpdate and useMassUpdate into
 * a single, config-driven hook that works with any entity type.
 *
 * Features:
 * - Detects single vs. mass update mode based on selection
 * - Normalizes field values for storage (UUID extraction)
 * - Updates grid rows immediately via apiRef
 * - Syncs React Query caches
 * - Supports custom single-row update handlers
 * - Works with real backend API or mock mode for testing
 */

import { useCallback, useRef, useState, useMemo } from 'react'
import { GridRowSelectionModel, GridApiPremium } from '@mui/x-data-grid-premium'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { EntityConfig, GridOptions } from '../types/EntityConfig'
import { normalizeFieldValue, getChangedFields, isValidValue } from '../utils/fieldUtils'
import { useCacheSync } from './useCacheSync'
import { useBatchUpdateItems } from 'api/aws/organizations_table/useBatchUpdateItems'

export interface BatchUpdatePayload {
  tableName: string
  PK: string
  SK: string
  fieldName: string
  newValue: any
}

interface UseUnifiedGridUpdateOptions<TRow extends Record<string, any>> {
  /** Entity configuration */
  config: EntityConfig<TRow>
  /** Current row selection model */
  rowSelectionModel: GridRowSelectionModel
  /** Grid API ref for direct row updates */
  apiRef: React.RefObject<GridApiPremium>
  /** Options data for lookups (users, contacts, etc.) */
  options?: GridOptions
  /** All rows in the grid (for entity lookup) */
  rows: TRow[]
  /** Callback when mass update completes */
  onMassUpdateComplete?: (count: number, field: string, value: any) => void
  /** Custom single-row update handler */
  customSingleRowUpdate?: (newRow: TRow, oldRow: TRow) => Promise<TRow>
  /** Mock mode - don't call real API, just update state */
  mockMode?: boolean
  /** Callback for mock mode to update local state */
  onMockUpdate?: (updates: BatchUpdatePayload[], normalizedFields: Record<string, any>) => void
  /** Show snackbar notifications */
  showNotifications?: boolean
  /** Snackbar function */
  showSnackbar?: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void
}

/**
 * Find an entity by various ID formats
 */
const findEntity = <TRow extends Record<string, any>>(
  entities: TRow[],
  id: string | number,
  config: EntityConfig<TRow>
): TRow | undefined => {
  const idStr = String(id)

  // Try to extract SK from composite ID format "PK::SK"
  const idParts = idStr.split('::')
  const possibleSK = idParts.length === 2 ? idParts[1] : idStr

  return entities.find(entity => {
    // Match by primary ID field
    if (entity[config.cache.primaryIdField] === idStr) return true
    // Match by secondary ID field
    if (config.cache.secondaryIdField && entity[config.cache.secondaryIdField] === idStr) return true
    // Match by extracted SK (for deals)
    if (entity.SK === possibleSK) return true
    // Match by row ID
    const rowId = config.getRowId(entity)
    if (String(rowId) === idStr) return true
    return false
  })
}

export const useUnifiedGridUpdate = <TRow extends Record<string, any>>(
  options: UseUnifiedGridUpdateOptions<TRow>
) => {
  const {
    config,
    rowSelectionModel,
    apiRef,
    rows,
    onMassUpdateComplete,
    customSingleRowUpdate,
    mockMode = false,
    onMockUpdate,
    showNotifications = true,
    showSnackbar,
  } = options

  const queryClient = useQueryClient()
  const { updateGridRowsDirectly, updateCache, applyUpdatesToRows } = useCacheSync(config)

  const isProcessingRef = useRef(false)
  const [isUpdating, setIsUpdating] = useState(false)

  // Real API mutation for production mode
  const { mutate: batchUpdate, isPending: isBatchUpdating } = useBatchUpdateItems()

  // Build field config map for quick lookup
  const fieldConfigMap = useMemo(() => {
    return new Map(config.fields.map(f => [f.field, f]))
  }, [config.fields])

  // Mock mutation for prototype/testing mode
  const mockMutation = useMutation({
    mutationFn: async (updates: BatchUpdatePayload[]) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300))
      return { success: true, updates }
    },
  })

  /**
   * Build update payloads for batch API
   */
  const buildUpdatePayloads = useCallback(
    (normalizedFields: Record<string, any>, selectionModel: GridRowSelectionModel): BatchUpdatePayload[] => {
      const updates: BatchUpdatePayload[] = []

      for (const [field, value] of Object.entries(normalizedFields)) {
        for (const id of selectionModel.ids) {
          const entity = findEntity(rows, id, config)
          if (!entity) {
            console.warn(`[useUnifiedGridUpdate] Entity not found for ID: ${id}`)
            continue
          }

          updates.push({
            tableName: config.api.tableName,
            PK: entity.PK || String(config.getRowId(entity)),
            SK: entity.SK || 'META',
            fieldName: field,
            newValue: value,
          })
        }
      }

      return updates
    },
    [rows, config]
  )

  /**
   * Main processRowUpdate handler
   */
  const processRowUpdate = useCallback(
    async (newRow: TRow, oldRow: TRow): Promise<TRow> => {
      // Prevent duplicate processing
      if (isProcessingRef.current) {
        return newRow
      }

      const selectedCount = rowSelectionModel?.ids?.size ?? 0
      const changedFields = getChangedFields(newRow, oldRow, config.editableFields)
      const changedFieldNames = Object.keys(changedFields)

      // No changes detected
      if (changedFieldNames.length === 0) {
        return newRow
      }

      // Single row update with custom handler
      if (selectedCount <= 1 && customSingleRowUpdate) {
        return customSingleRowUpdate(newRow, oldRow)
      }

      // Mass update mode (or single row without custom handler)
      isProcessingRef.current = true
      setIsUpdating(true)

      try {
        // Normalize all changed field values
        const normalizedFields: Record<string, any> = {}
        for (const [field, value] of Object.entries(changedFields)) {
          const fieldConfig = fieldConfigMap.get(field)
          const normalized = normalizeFieldValue(field, value, fieldConfig)
          if (isValidValue(normalized) || normalized === null || (Array.isArray(normalized) && normalized.length === 0)) {
            normalizedFields[field] = normalized
          }
        }

        if (Object.keys(normalizedFields).length === 0) {
          if (showNotifications && showSnackbar) {
            showSnackbar('No valid updates to apply', 'error')
          }
          return newRow
        }

        // Build update payloads
        const updates = buildUpdatePayloads(normalizedFields, rowSelectionModel)

        if (updates.length === 0) {
          if (showNotifications && showSnackbar) {
            showSnackbar('No valid updates to apply', 'error')
          }
          return newRow
        }

        // Build row updates for direct grid update
        const gridRowUpdates = Array.from(rowSelectionModel.ids).map(id => ({
          id: id as string | number,
          rowId: id,
          ...normalizedFields,
        }))

        if (mockMode) {
          // Mock mode - simulate API call
          await mockMutation.mutateAsync(updates)

          // Update grid rows directly
          updateGridRowsDirectly(apiRef, gridRowUpdates)

          // Call mock update handler
          if (onMockUpdate) {
            onMockUpdate(updates, normalizedFields)
          }

          // Show success notification
          if (showNotifications && showSnackbar) {
            const count = rowSelectionModel.ids.size
            showSnackbar(
              count === 1
                ? `${config.labels.singular.charAt(0).toUpperCase() + config.labels.singular.slice(1)} updated`
                : `${count} ${config.labels.plural} updated`,
              'success'
            )
          }

          // Notify caller
          if (onMassUpdateComplete && selectedCount > 1) {
            const fieldName = changedFieldNames[0]
            onMassUpdateComplete(selectedCount, fieldName, normalizedFields[fieldName])
          }
        } else {
          // Real mode - call actual backend API
          // Optimistically update grid rows immediately for instant feedback
          updateGridRowsDirectly(apiRef, gridRowUpdates)

          // Call the batch update API
          batchUpdate(updates as any, {
            onSuccess: () => {
              // Update React Query caches after successful API call
              updateCache(queryClient, updates, normalizedFields)

              if (showNotifications && showSnackbar) {
                const count = rowSelectionModel.ids.size
                showSnackbar(
                  count === 1
                    ? `${config.labels.singular.charAt(0).toUpperCase() + config.labels.singular.slice(1)} updated`
                    : `${count} ${config.labels.plural} updated`,
                  'success'
                )
              }

              if (onMassUpdateComplete && selectedCount > 1) {
                const fieldName = changedFieldNames[0]
                onMassUpdateComplete(selectedCount, fieldName, normalizedFields[fieldName])
              }
            },
            onError: (error) => {
              console.error('[useUnifiedGridUpdate] API update failed:', error)
              if (showNotifications && showSnackbar) {
                showSnackbar('Update failed', 'error')
              }
              // Note: Grid was already updated optimistically, would need to revert here
              // For now, rely on cache invalidation on next fetch
            },
          })
        }

        return newRow
      } catch (error) {
        console.error('[useUnifiedGridUpdate] Update failed:', error)
        if (showNotifications && showSnackbar) {
          showSnackbar('Update failed', 'error')
        }
        return oldRow // Revert on error
      } finally {
        isProcessingRef.current = false
        setIsUpdating(false)
      }
    },
    [
      rowSelectionModel,
      config,
      fieldConfigMap,
      customSingleRowUpdate,
      buildUpdatePayloads,
      mockMode,
      mockMutation,
      apiRef,
      updateGridRowsDirectly,
      updateCache,
      queryClient,
      onMockUpdate,
      onMassUpdateComplete,
      showNotifications,
      showSnackbar,
    ]
  )

  return {
    processRowUpdate,
    isUpdating: isUpdating || isBatchUpdating,
  }
}

export default useUnifiedGridUpdate
